<template>
  <!-- 필터 모달 컨테이너 - 드롭다운 형태 모달 -->
  <div class="filter-container" ref="containerRef">
    <!-- 트리거 버튼 슬롯 - 외부에서 버튼 커스터마이징 가능 -->
    <slot name="trigger" :toggle="toggleFilterModal" :isOpen="isFilterModalOpen">
      <!-- <button class="filter-trigger-btn" @click="toggleFilterModal"></button> -->
    </slot>

    <!-- Portal로 body에 렌더링 - z-index 관리 위해 -->
    <Teleport to="body">
      <!-- 배경 오버레이 - closeOnBackdropClick 이 false 면 바깥 클릭으로 닫지 않음 -->
      <div v-if="isFilterModalOpen" class="filter-overlay" @click="closeOnBackdropClick && closeFilterModal()"></div>
      <!-- 실제 모달 드롭다운 -->
      <div
        v-if="isFilterModalOpen"
        ref="dropdownRef"
        class="filter-dropdown"
        :style="calculatedPosition"
        :class="[
          {
            'right-aligned': position === 'right',
            'left-aligned': position === 'left',
            'filter-dropdown--small': size === 'small',
            'filter-dropdown--medium': size === 'medium',
            'filter-dropdown--large': size === 'large',
            'filter-dropdown--xlarge': size === 'xlarge',
          },
          modalClass,
        ]"
      >
        <!-- 모달 헤더 - 제목과 닫기 버튼 -->
        <div v-if="!hideHeader" class="filter-header">
          <h3>{{ title }}</h3>
          <div class="flex gap-25 items-center">
            <!-- 헤더 액션 슬롯 - 추가 버튼들 -->
            <slot name="headerActions" />
            <!-- 닫기 버튼 -->
            <button class="close-button" @click="closeFilterModal">
              <i class="icon icon-md icon-close"></i>
            </button>
          </div>
        </div>

        <!-- 모달 본문 - 실제 필터 내용 -->
        <div class="filter-body" :class="{ 'is-scroll': isScroll }">
          <!-- 고정 액션 슬롯 - 상단 고정 버튼들 -->
          <slot name="fixedActions" />
          <!-- 기본 컨텐츠 슬롯 -->
          <slot />
        </div>

        <!-- 모달 푸터 - 적용/취소 버튼 영역 -->
        <div v-if="showFooter" class="filter-footer" :style="{ background: bgColor }">
          <!-- 푸터 액션 슬롯 - 저장/취소 등 버튼 -->
          <slot name="footerActions" />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, nextTick, watch, onMounted, onUnmounted } from 'vue';

/**
 * UiModal 루트(.ui-popup) 기본 z-index(1000)보다 위에 보이도록 함.
 * 드롭다운에 인라인 z-index가 항상 붙으므로 SCSS z-index:9999는 실제로는 덮어써짐(999) → 모달에 가려지던 이슈.
 */
const FILTER_OVERLAY_Z = 10050;
const FILTER_DROPDOWN_Z = 10051;

/**
 * 모달 상태 관리
 * 목적: 모달 열림/닫힘 상태와 위치 정보 관리
 */
const isFilterModalOpen = ref(false);
const calculatedPosition = ref({});
const containerRef = ref(null); // 트리거 요소 참조
const dropdownRef = ref(null); // 렌더된 드롭다운 요소 참조

/**
 * 컴포넌트 props 정의
 * 목적: 외부에서 모달의 스타일과 동작 제어
 */
const props = defineProps({
  bgColor: {
    type: String,
    default: '#fff',
  },
  title: {
    type: String,
    default: '검색 필터',
  },
  position: {
    type: String,
    default: 'left',
    validator: value => ['left', 'right'].includes(value),
  },
  showFooter: {
    type: Boolean,
    default: false,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
    validator: value => ['small', 'medium', 'large', 'xlarge'].includes(value),
  },
  isScroll: {
    type: Boolean,
    default: false,
  },
  offsetX: {
    type: Number,
    default: 35,
  },
  offsetY: {
    type: Number,
    default: 10,
  },
  specialPosition: {
    type: [Boolean, Object],
    default: false,
    // 사용 예: { top: 27, left: -25 } 또는 true (기본값 사용)
  },
  modalClass: {
    type: String,
    default: '',
  },
  autoFlip: {
    type: Boolean,
    default: false,
    // true로 설정 시 화면 경계를 체크하여 모달이 화면 밖으로 나가지 않도록 자동 조정
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['reset', 'apply', 'update:modelValue']);

let dropdownResizeObserver = null;

const detachDropdownResizeObserver = () => {
  if (dropdownResizeObserver) {
    dropdownResizeObserver.disconnect();
    dropdownResizeObserver = null;
  }
};

/** 아코디언·셀렉트 등으로 높이가 변할 때 autoFlip 위치 재계산 */
const attachDropdownResizeObserver = () => {
  detachDropdownResizeObserver();
  if (!props.autoFlip || !dropdownRef.value) return;

  dropdownResizeObserver = new ResizeObserver(() => {
    if (isFilterModalOpen.value) {
      updatePosition();
    }
  });
  dropdownResizeObserver.observe(dropdownRef.value);
};

/**
 * v-model 양방향 바인딩 관리
 * 목적: 부모 컴포넌트와 모달 상태 동기화
 */
watch(
  () => props.modelValue,
  newValue => {
    isFilterModalOpen.value = newValue;
    if (newValue) {
      updatePosition();
      // Teleport·아코디언 레이아웃 안정 후 재측정 + 높이 변화 감시
      nextTick(() => {
        requestAnimationFrame(() => {
          updatePosition();
          attachDropdownResizeObserver();
        });
      });
    } else {
      detachDropdownResizeObserver();
    }
  },
);

watch(isFilterModalOpen, newValue => {
  emit('update:modelValue', newValue);
});

/** 트리거 버튼 기준 rect (없으면 container) */
function getAnchorRect() {
  const container = containerRef.value;
  if (!container) return null;

  const trigger = container.querySelector('button:not(.close-button), [data-filter-trigger]');
  return (trigger || container).getBoundingClientRect();
}

/**
 * 모달 위치 계산 함수
 * 목적: 트리거 요소 기준으로 드롭다운 위치 설정
 */
function updatePosition() {
  if (!containerRef.value) return;

  nextTick(() => {
    const rect = getAnchorRect();
    if (!rect) return;

    // 스크롤 오프셋 포함하여 절대 좌표 계산
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // 특수 위치 조정값 계산
    let topOffset = 0;
    let leftOffset = 0;

    if (props.specialPosition) {
      if (typeof props.specialPosition === 'object') {
        // 객체로 전달된 경우 사용자 정의 값 사용
        topOffset = props.specialPosition.top || 0;
        leftOffset = props.specialPosition.left || 0;
      } else {
        // Boolean true인 경우 기본값 사용
        topOffset = 27;
        leftOffset = -25;
      }
    }

    // autoFlip="true" 활성화 시 viewport 체크크
    if (props.autoFlip) {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownEl = dropdownRef.value;

      // 모달 크기 (실측 우선, 실패 시 추정값)
      const modalWidthMap = { small: 300, medium: 400, large: 700 };
      const modalWidth = dropdownEl?.offsetWidth || modalWidthMap[props.size] || 400;
      const modalHeight = dropdownEl?.offsetHeight || 320;

      const verticalGap = Number.isFinite(props.offsetY) ? props.offsetY : 10;
      let topPosition = rect.bottom + verticalGap + topOffset;

      // 화면 하단 체크
      if (topPosition + modalHeight > viewportHeight) {
        topPosition = rect.top - modalHeight - verticalGap + topOffset;
        if (topPosition < 0) {
          topPosition = Math.max(verticalGap, viewportHeight - modalHeight - verticalGap);
        }
      }

      if (props.position === 'right') {
        const edgeMargin = 10;
        const shiftRatio = 1.05;
        let leftPosition = rect.right + props.offsetX + leftOffset;
        let transform = 'translateX(-105%)';

        // translateX(-105%) 적용 후 왼쪽이 뷰포트를 벗어나면 트리거 오른쪽으로 펼침
        const effectiveLeft = leftPosition - modalWidth * shiftRatio;
        if (effectiveLeft < edgeMargin) {
          leftPosition = rect.left + leftOffset;
          transform = 'none';
          if (leftPosition < edgeMargin) {
            leftPosition = edgeMargin;
          }
          if (leftPosition + modalWidth > viewportWidth - edgeMargin) {
            leftPosition = Math.max(edgeMargin, viewportWidth - modalWidth - edgeMargin);
          }
        } else if (leftPosition > viewportWidth - edgeMargin) {
          leftPosition = viewportWidth - edgeMargin;
        }

        calculatedPosition.value = {
          position: 'fixed',
          top: `${topPosition}px`,
          left: `${leftPosition}px`,
          transform,
          zIndex: FILTER_DROPDOWN_Z,
        };
      } else {
        let leftPosition = rect.left + leftOffset;
        if (leftPosition < 0) {
          leftPosition = 10;
        }
        if (leftPosition + modalWidth > viewportWidth) {
          leftPosition = viewportWidth - modalWidth - 10;
        }

        calculatedPosition.value = {
          position: 'fixed',
          top: `${topPosition}px`,
          left: `${leftPosition}px`,
          zIndex: FILTER_DROPDOWN_Z,
        };
      }
      return;
    }

    // 기본 동작 (autoFlip="false" 비활성화)
    // 우측 정렬일 경우
    if (props.position === 'right') {
      calculatedPosition.value = {
        position: 'absolute',
        top: `${rect.bottom + scrollTop + props.offsetY + topOffset}px`,
        left: `${rect.right + scrollLeft + props.offsetX + leftOffset}px`,
        transform: 'translateX(-105%)',
        zIndex: FILTER_DROPDOWN_Z,
      };
    } else {
      // 좌측 정렬 (기본)
      calculatedPosition.value = {
        position: 'absolute',
        top: `${rect.bottom + scrollTop + props.offsetY + topOffset}px`,
        left: `${rect.left + scrollLeft + leftOffset}px`,
        zIndex: FILTER_DROPDOWN_Z,
      };
    }
  });
}

/**
 * 스크롤/리사이즈 시 위치 업데이트
 * 목적: 화면 변화시 모달 위치 재조정
 */
function handlePositionUpdate() {
  if (isFilterModalOpen.value) {
    updatePosition();
  }
}

/**
 * 이벤트 리스너 등록
 * 목적: 스크롤, 리사이즈 시 모달 위치 업데이트
 */
onMounted(() => {
  window.addEventListener('scroll', handlePositionUpdate);
  window.addEventListener('resize', handlePositionUpdate);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handlePositionUpdate);
  window.removeEventListener('resize', handlePositionUpdate);
  detachDropdownResizeObserver();
});

/**
 * 모달 토글 함수
 * 목적: 모달 열기/닫기 및 위치 업데이트
 */
function toggleFilterModal() {
  isFilterModalOpen.value = !isFilterModalOpen.value;
  if (isFilterModalOpen.value) {
    updatePosition();
  }
}

/**
 * 모달 닫기 함수
 * 목적: 모달 상태를 false로 변경
 */
function closeFilterModal() {
  isFilterModalOpen.value = false;
}

/**
 * 필터 초기화 함수
 * 목적: 필터 값들을 초기화하고 모달 닫기
 */
function resetFilters() {
  emit('reset');
  closeFilterModal();
}

/**
 * 필터 적용 함수
 * 목적: 설정된 필터를 적용하고 모달 닫기
 */
function applyFilters() {
  emit('apply');
  closeFilterModal();
}

// 외부에서 접근 가능한 메서드 노출
defineExpose({
  toggleFilterModal,
  closeFilterModal,
});
</script>

<style lang="scss" scoped>
.filter-container {
  display: inline-flex;
  align-items: center;
  position: relative;
  line-height: 0;
}

// 기본 트리거 버튼 스타일
.filter-trigger-btn {
  padding: 8px 16px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #e9e9e9;
  }
}

// 전역 스타일 - Teleport로 body에 렌더링되므로 scoped 제외
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: v-bind('FILTER_OVERLAY_Z');
}

// 필터 드롭다운 메인 컨테이너
.filter-dropdown {
  position: absolute;
  min-width: 400px;
  max-width: calc(100vw - 20px);
  box-sizing: border-box;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10051;

  // 사이즈별 최소 너비 설정
  &--small {
    width: 300px;
    min-width: 300px;
    max-width: min(300px, calc(100vw - 20px));
  }

  &--medium {
    width: 450px;
    min-width: 450px;
    max-width: min(450px, calc(100vw - 20px));
  }

  &--large {
    width: 700px;
    min-width: 700px;
    max-width: min(700px, calc(100vw - 20px));
  }

  &--xlarge {
    width: 960px;
    min-width: 960px;
    max-width: min(960px, calc(100vw - 20px));
  }
}

// 필터 헤더 - 제목과 닫기 버튼 영역
.filter-header {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  align-items: center;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }

  .close-button {
    width: 20px;
    height: 20px;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f5f5f5;
      border-radius: 2px;
    }
  }
}

// 필터 본문 - 실제 필터 컨텐츠 영역
.filter-body {
  padding: 16px;
  min-width: 0;
  overflow-x: hidden;

  // 스크롤 옵션 활성화시 최대 높이 설정
  &.is-scroll {
    max-height: 400px;
    overflow-y: auto;
  }
}

// 필터 푸터 - 액션 버튼들 영역
.filter-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #eee;
}
</style>
